/**
 * Build graphs from blast_radius_scores reachable caches
 * (compact predecessor walk + persisted business/adversarial overlays).
 */

import type { BlastRadiusScoreDocument, ReachableLayerItem } from '../../api-utils/blast';
import { blastElementId } from '../../api-utils/blast';
import type {
  BusinessElementDocument,
  ThreatElementDocument,
} from '../../api-utils/elements';
import { flattenIdList, sourceRefId } from '../../lib/funnel';
import type { ForceGraphData, ForceGraphLink, ForceGraphNode } from './correlationGraph';

export interface EbrTreeNode {
  id: string;
  hops?: number | string;
  pred?: string | null;
  edge?: string;
}

export function parseNodeRef(raw: string): { ring: string; id: string } {
  const text = String(raw || '').trim();
  const slash = text.indexOf('/');
  if (slash <= 0) return { ring: 'infrastructure_elements', id: text };
  return { ring: text.slice(0, slash), id: text.slice(slash + 1) };
}

function hopsOf(row: EbrTreeNode): number {
  const n = Number(row.hops);
  return Number.isFinite(n) ? n : 0;
}

export function ebrTreeRows(score: BlastRadiusScoreDocument | null | undefined): EbrTreeNode[] {
  const infra = score?.reachable_infrastructure_elements;
  const tree =
    infra && Array.isArray(infra.tree) && infra.tree.length
      ? infra.tree
      : score?.reachable_nodes?.tree;
  return Array.isArray(tree) ? (tree as EbrTreeNode[]) : [];
}

/** Force-graph of the EBR predecessor tree; labels/domains from inventory when present. */
export function buildEbrTreeGraph(
  score: BlastRadiusScoreDocument,
  inventory: ForceGraphNode[] = []
): ForceGraphData {
  const tree = ebrTreeRows(score);
  const invById = new Map(inventory.map((node) => [node.id, node]));
  const nodesById = new Map<string, ForceGraphNode>();
  const links: ForceGraphLink[] = [];
  const seenLinks = new Set<string>();

  const startId = blastElementId(score);

  const ensure = (rawId: string, hops?: number): ForceGraphNode | null => {
    const { id } = parseNodeRef(rawId);
    if (!id) return null;
    const existing = nodesById.get(id);
    if (existing) {
      if (hops != null && hops > 0 && existing.attackStep == null) {
        existing.attackStep = hops;
      }
      return existing;
    }
    const inv = invById.get(id);
    const isStart = Boolean(startId) && id === startId;
    const fallbackLabel = isStart ? String(score.element_name || id) : id.slice(0, 8);
    const node: ForceGraphNode = {
      id,
      label: inv?.label || inv?.name || fallbackLabel,
      group: inv?.group || String(score.universal_domain || score.max_domain || 'other').toLowerCase(),
      universalType: inv?.universalType || 'element',
      universalDomain: inv?.universalDomain || inv?.group || String(score.universal_domain || 'other'),
      provider: inv?.provider || '',
      providerType: inv?.providerType || '',
      externalId: inv?.externalId || id,
      name: inv?.name || inv?.label || fallbackLabel,
      linkCount: 0,
      layer: 'infrastructure',
      isBlastOrigin: isStart,
      attackStep: hops != null && hops > 0 ? hops : undefined,
    };
    nodesById.set(id, node);
    return node;
  };

  for (const row of tree) {
    ensure(row.id, hopsOf(row));
  }

  // Ensure start even if tree omitted it.
  if (startId) {
    const start = ensure(startId, 0);
    if (start) start.isBlastOrigin = true;
  }

  for (const row of tree) {
    const child = parseNodeRef(row.id).id;
    const predRaw = row.pred == null || row.pred === '' ? '' : String(row.pred);
    if (!child || !predRaw) continue;
    const parent = parseNodeRef(predRaw).id;
    if (!parent || parent === child) continue;
    ensure(parent);
    ensure(child, hopsOf(row));
    const key = `${parent}->${child}`;
    if (seenLinks.has(key)) continue;
    seenLinks.add(key);
    links.push({
      source: parent,
      target: child,
      label: String(row.edge || 'REACHES'),
    });
  }

  const nodes = Array.from(nodesById.values()).map((node) => ({
    ...node,
    linkCount: links.reduce((n, link) => {
      const s = typeof link.source === 'string' ? link.source : String(link.source);
      const t = typeof link.target === 'string' ? link.target : String(link.target);
      return n + (s === node.id || t === node.id ? 1 : 0);
    }, 0),
  }));

  return {
    nodes,
    links,
    stats: {
      elementCount: nodes.length,
      nodeCount: nodes.length,
      linkCount: links.length,
      crossLayerLinkCount: 0,
    },
  };
}

export type EbrWalkLayer = 'infrastructure' | 'business' | 'threat';

export interface EbrWalkHierarchyNode {
  name: string;
  id: string;
  layer: EbrWalkLayer;
  unconfirmed?: boolean;
  children?: EbrWalkHierarchyNode[];
}

export interface CrownPathSegment {
  from: string;
  to: string;
}

export function layerFromQualifiedId(qualified: string): EbrWalkLayer {
  const ring = parseNodeRef(qualified).ring;
  if (ring === 'business_elements') return 'business';
  if (ring === 'threat_elements') return 'threat';
  return 'infrastructure';
}

export function qualifyNodeId(raw: string, fallbackRing = 'infrastructure_elements'): string {
  const parsed = parseNodeRef(raw);
  const id = parsed.id;
  if (!id) return '';
  const ring = raw.includes('/') ? parsed.ring : fallbackRing;
  return `${ring}/${id}`;
}

function hopMap(tree: EbrTreeNode[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of tree) {
    const id = qualifyNodeId(row.id);
    if (id) out.set(id, hopsOf(row));
  }
  return out;
}

function labelFor(
  qualified: string,
  labels: ReadonlyMap<string, string>,
  fallback?: string
): string {
  const { id } = parseNodeRef(qualified);
  return labels.get(qualified) || labels.get(id) || fallback || id || qualified;
}

function wouldCycle(parentOf: Map<string, string>, parent: string, child: string): boolean {
  let cursor = parent;
  const guard = new Set<string>();
  while (cursor) {
    if (cursor === child) return true;
    if (guard.has(cursor)) return true;
    guard.add(cursor);
    cursor = parentOf.get(cursor) || '';
  }
  return false;
}

/** Left-to-right predecessor tree from the persisted walk cache. */
export function buildEbrWalkHierarchy(
  score: BlastRadiusScoreDocument,
  labels: ReadonlyMap<string, string> = new Map()
): EbrWalkHierarchyNode | null {
  const tree = ebrTreeRows(score);
  const startBare = blastElementId(score);
  const startId =
    qualifyNodeId(
      tree.find((row) => hopsOf(row) === 0)?.id ||
        (startBare ? `infrastructure_elements/${startBare}` : '')
    ) || (startBare ? `infrastructure_elements/${startBare}` : '');
  if (!startId && tree.length === 0) return null;

  const rootId = startId || qualifyNodeId(tree[0]?.id || '');
  if (!rootId) return null;

  const parentOf = new Map<string, string>();
  const childrenOf = new Map<string, string[]>();
  const addChild = (parent: string, child: string) => {
    if (!parent || !child || parent === child || parentOf.has(child)) return;
    if (wouldCycle(parentOf, parent, child)) return;
    parentOf.set(child, parent);
    const list = childrenOf.get(parent) || [];
    list.push(child);
    childrenOf.set(parent, list);
  };

  const numbered = tree
    .map((row) => qualifyNodeId(row.id))
    .filter((id) => id && id !== rootId);
  for (const row of tree) {
    const id = qualifyNodeId(row.id);
    if (!id || id === rootId) continue;
    const pred = row.pred ? qualifyNodeId(String(row.pred)) : '';
    if (pred && (pred === rootId || numbered.includes(pred))) addChild(pred, id);
  }
  for (const id of numbered) {
    if (!parentOf.has(id)) addChild(rootId, id);
  }

  const visiting = new Set<string>();
  const build = (id: string): EbrWalkHierarchyNode => {
    if (visiting.has(id)) {
      return {
        name: labelFor(id, labels),
        id,
        layer: layerFromQualifiedId(id),
      };
    }
    visiting.add(id);
    const childIds = (childrenOf.get(id) || []).slice();
    const children = childIds.filter((childId) => !visiting.has(childId)).map(build);
    visiting.delete(id);
    return {
      name: labelFor(id, labels, id === rootId ? String(score.element_name || '') : undefined),
      id,
      layer: layerFromQualifiedId(id),
      children: children.length ? children : undefined,
    };
  };

  return build(rootId);
}

function findNode(root: EbrWalkHierarchyNode, id: string): EbrWalkHierarchyNode | null {
  if (root.id === id) return root;
  for (const child of root.children || []) {
    const hit = findNode(child, id);
    if (hit) return hit;
  }
  return null;
}

function collectIds(root: EbrWalkHierarchyNode, into: Set<string> = new Set()): Set<string> {
  into.add(root.id);
  for (const child of root.children || []) collectIds(child, into);
  return into;
}

function attachChild(parent: EbrWalkHierarchyNode, child: EbrWalkHierarchyNode) {
  const kids = parent.children || [];
  if (kids.some((item) => item.id === child.id)) return;
  parent.children = [...kids, child];
}

function deepestMatchingInfra(
  refs: string[],
  hops: Map<string, number>
): string | null {
  let best: string | null = null;
  let bestHop = -1;
  for (const raw of refs) {
    const qualified = qualifyNodeId(raw);
    if (!hops.has(qualified)) continue;
    const hop = hops.get(qualified) ?? 0;
    if (hop >= bestHop) {
      bestHop = hop;
      best = qualified;
    }
  }
  return best;
}

/**
 * Hang inventory docs that infrastructure_refs a reached infra.
 * Used for the unconfirmed-matches overlay; skips ids already on the tree.
 */
export function attachReachOverlays(
  root: EbrWalkHierarchyNode,
  score: BlastRadiusScoreDocument,
  business: BusinessElementDocument[],
  threats: ThreatElementDocument[],
  labels: ReadonlyMap<string, string>,
  opts?: { includeThreats?: boolean; unconfirmed?: boolean }
): EbrWalkHierarchyNode {
  const hops = hopMap(ebrTreeRows(score));
  const existing = collectIds(root);
  const unconfirmed = opts?.unconfirmed === true;
  const includeThreats = opts?.includeThreats !== false;

  const hang = (
    docs: Array<{ _id?: string; name?: string; infrastructure_refs?: unknown[] }>,
    ring: 'business_elements' | 'threat_elements',
    layer: EbrWalkLayer
  ) => {
    for (const doc of docs) {
      const bare = String(doc._id || '').trim();
      if (!bare) continue;
      const qualified = `${ring}/${bare}`;
      if (existing.has(qualified)) continue;
      const refs = flattenIdList(doc.infrastructure_refs).map((id) => qualifyNodeId(id));
      const parentId = deepestMatchingInfra(refs, hops);
      if (!parentId) continue;
      const parent = findNode(root, parentId);
      if (!parent) continue;
      attachChild(parent, {
        name: labels.get(qualified) || labels.get(bare) || String(doc.name || bare),
        id: qualified,
        layer,
        unconfirmed: unconfirmed || undefined,
      });
      existing.add(qualified);
    }
  };

  hang(business, 'business_elements', 'business');
  if (includeThreats) hang(threats, 'threat_elements', 'threat');
  return root;
}

function layerItems(raw: unknown): ReachableLayerItem[] {
  if (!isRecord(raw) || !Array.isArray(raw.items)) return [];
  return raw.items.filter((item): item is ReachableLayerItem => isRecord(item));
}

function hangPersistedLayer(
  root: EbrWalkHierarchyNode,
  items: ReachableLayerItem[],
  layer: EbrWalkLayer,
  fallbackRing: 'business_elements' | 'threat_elements',
  labels: ReadonlyMap<string, string>,
  hops: Map<string, number>,
  existing: Set<string>
) {
  for (const item of items) {
    const qualified = qualifyNodeId(String(item.id || ''), fallbackRing);
    if (!qualified || existing.has(qualified)) continue;
    const parentId = deepestMatchingInfra(item.matched || [], hops);
    if (!parentId) continue;
    const parent = findNode(root, parentId);
    if (!parent) continue;
    const { id: bare } = parseNodeRef(qualified);
    attachChild(parent, {
      name: labels.get(qualified) || labels.get(bare) || String(item.name || bare),
      id: qualified,
      layer,
    });
    existing.add(qualified);
  }
}

export interface AssembleEbrWalkOptions {
  showAdversarial?: boolean;
  showUnconfirmed?: boolean;
  business?: BusinessElementDocument[];
  threats?: ThreatElementDocument[];
}

/** Confirmed handler caches, then optional inventory join for unconfirmed extras. */
export function assembleEbrWalkHierarchy(
  score: BlastRadiusScoreDocument,
  labels: ReadonlyMap<string, string>,
  opts?: AssembleEbrWalkOptions
): EbrWalkHierarchyNode | null {
  const root = buildEbrWalkHierarchy(score, labels);
  if (!root) return null;
  const hops = hopMap(ebrTreeRows(score));
  const existing = collectIds(root);
  hangPersistedLayer(
    root,
    layerItems(score.reachable_business_elements),
    'business',
    'business_elements',
    labels,
    hops,
    existing
  );
  if (opts?.showAdversarial) {
    hangPersistedLayer(
      root,
      layerItems(score.reachable_adversarial_elements),
      'threat',
      'threat_elements',
      labels,
      hops,
      existing
    );
  }
  if (opts?.showUnconfirmed) {
    attachReachOverlays(root, score, opts.business || [], opts.threats || [], labels, {
      includeThreats: Boolean(opts.showAdversarial),
      unconfirmed: true,
    });
  }
  return root;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function hopList(raw: unknown): Array<{ from?: string; to?: string }> {
  if (!Array.isArray(raw)) return [];
  return raw.filter((item) => isRecord(item)) as Array<{ from?: string; to?: string }>;
}

/** Crown path from a persisted score or a live run_forward payload. */
export function extractCrownPathHighlight(
  source: BlastRadiusScoreDocument | Record<string, unknown> | null | undefined
): { segments: CrownPathSegment[]; crownId: string | null } {
  if (!source || typeof source !== 'object') {
    return { segments: [], crownId: null };
  }
  const rec = source as Record<string, unknown>;
  const severity = isRecord(rec.severity) ? rec.severity : {};
  const hops = hopList(rec.path_to_crown_jewel || severity.path_to_crown_jewel);
  const jewel = isRecord(rec.crown_jewel)
    ? rec.crown_jewel
    : isRecord(severity.crown_jewel)
      ? severity.crown_jewel
      : null;
  const crownRaw = jewel?.node_id ?? rec.crown_jewel_id;
  const crownId = qualifyNodeId(sourceRefId(crownRaw));
  const segments: CrownPathSegment[] = [];
  for (const hop of hops) {
    const from = qualifyNodeId(String(hop.from || ''));
    const to = qualifyNodeId(String(hop.to || ''));
    if (from && to) segments.push({ from, to });
  }
  const lastInfra = segments.length ? segments[segments.length - 1].to : '';
  if (crownId && lastInfra && crownId !== lastInfra) {
    segments.push({ from: lastInfra, to: crownId });
  }
  return { segments, crownId: crownId || null };
}

/** If the jewel is not in the tree, hang it off the last crown-path infra hop. */
export function ensureCrownJewelNode(
  root: EbrWalkHierarchyNode,
  crownId: string | null,
  segments: CrownPathSegment[],
  labels: ReadonlyMap<string, string>
): EbrWalkHierarchyNode {
  if (!crownId || findNode(root, crownId)) return root;
  const last = segments.length ? segments[segments.length - 1] : null;
  const parentId = last && last.to === crownId ? last.from : last?.to;
  if (!parentId) return root;
  const parent = findNode(root, parentId);
  if (!parent) return root;
  attachChild(parent, {
    name: labelFor(crownId, labels, 'Crown jewel'),
    id: crownId,
    layer: layerFromQualifiedId(crownId),
  });
  return root;
}

export function unwrapHandlerRecord(data: unknown): Record<string, unknown> | null {
  let current: unknown = data;
  for (let i = 0; i < 6; i++) {
    if (Array.isArray(current)) {
      current = current.length > 0 ? current[0] : null;
      continue;
    }
    if (!isRecord(current)) return null;
    if (current.starting_node || current.reachable_infra_subgraph || current.path_to_crown_jewel) {
      return current;
    }
    if (isRecord(current.result)) {
      current = current.result;
      continue;
    }
    if ('output' in current) {
      current = current.output;
      continue;
    }
    break;
  }
  return isRecord(current) ? current : null;
}
